import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DashNav from "@/components/chalann/DashNav";
import { listFiados, paySale, formatMXN, getStore } from "@/lib/api";
import { Check, User, Calendar } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Fiados() {
  const nav = useNavigate();
  const store = getStore();
  const [fiados, setFiados] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!store?.id) return nav("/setup");
    load();
  }, [nav, store?.id]);

  const load = async () => {
    setLoading(true);
    try { setFiados(await listFiados()); }
    catch { toast.error("No se pudieron cargar los fiados"); }
    finally { setLoading(false); }
  };

  const cobrar = async (id, name) => {
    if (!window.confirm(`¿Marcar como pagado el fiado de ${name || "este cliente"}?`)) return;
    try {
      await paySale(id);
      toast.success("¡Fiado saldado!");
      load();
    } catch { toast.error("No se pudo actualizar"); }
  };

  const total = fiados.reduce((s, f) => s + f.total_amount, 0);
  const byCustomer = fiados.reduce((acc, f) => {
    const k = f.customer_name || "Sin nombre";
    acc[k] = (acc[k] || 0) + f.total_amount;
    return acc;
  }, {});

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="fiados">
      <div className="grain-overlay" />
      <DashNav />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <div className="mb-8">
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2">/ Cuentas por cobrar</p>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none">
            Lo que te <span className="text-[#ff5a00]">deben.</span>
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#ff5a00]/[0.15] to-transparent p-6 md:p-10 overflow-hidden mb-8"
        >
          <p className="font-mono text-[11px] tracking-widest text-white/50 uppercase mb-3">Total pendiente</p>
          <p className="font-display text-6xl md:text-7xl tracking-tighter text-[#ff5a00]" data-testid="fiados-total">
            {formatMXN(total)}
          </p>
          <p className="mt-2 text-white/50 text-sm">
            {fiados.length} fiados · {Object.keys(byCustomer).length} clientes distintos
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center text-white/40 py-16">Cargando…</div>
        ) : fiados.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-12 text-center">
            <p className="font-display text-3xl tracking-tighter">Nadie te debe nada.</p>
            <p className="text-white/50 text-sm mt-2">Los fiados aparecerán aquí cuando cobres a crédito desde el POS.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {fiados.map((f) => (
              <div key={f.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 md:p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid={`fiado-${f.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-[#ff5a00]" />
                    <span className="font-display text-xl tracking-tight">{f.customer_name || "Cliente sin nombre"}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap text-xs text-white/50 font-mono">
                    <span className="flex items-center gap-1"><Calendar size={11} /> {new Date(f.created_at).toLocaleString("es-MX")}</span>
                    <span>·</span>
                    <span>{f.items.length} artículos</span>
                  </div>
                  <p className="mt-2 text-sm text-white/60 line-clamp-1">
                    {f.items.map((i) => `${i.quantity}× ${i.name}`).join(" · ")}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-display text-2xl tracking-tighter">{formatMXN(f.total_amount)}</span>
                  <button
                    onClick={() => cobrar(f.id, f.customer_name)}
                    className="btn-primary text-sm"
                    data-testid={`fiado-pay-${f.id}`}
                  >
                    <Check size={14} /> Cobrar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Toaster theme="dark" position="top-center" />
    </main>
  );
}
